import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

/**
 * Documents the entity references by link rather than attachment.
 *
 * Share-notification emails carry no file, so `attachments` is empty and the
 * document itself is only reachable through the URL in the body — which the
 * plain-text rendering drops. Surfacing it lets a reviewer open the document
 * the evidence is actually about.
 */
export default function EntityDocumentLinks({ links, tc, theme }) {
  if (!links?.length) return null;

  return (
    <Box sx={{ borderTop: `1px solid ${tc.border.subtle}`, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
        <LinkIcon sx={{ fontSize: 16, color: tc.text.secondary }} />
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: tc.text.secondary }}>
          {links.length} linked document{links.length !== 1 ? "s" : ""}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {links.map((link) => (
          <Tooltip key={link.url} title={link.url} placement="top" enterDelay={600}>
            <Box
              component="a"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                border: `1px solid ${tc.border.main}`,
                bgcolor: tc.background.infoBox,
                textDecoration: "none",
                maxWidth: 320,
                "&:hover": { borderColor: tc.border.strong, backgroundColor: tc.background.hover },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 500,
                    color: tc.text.strong,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {link.label}
                </Typography>
                <Typography sx={{ fontSize: "0.68rem", color: tc.text.secondary }}>
                  {link.source}
                </Typography>
              </Box>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  p: 0.5,
                  ml: 0.25,
                  borderRadius: "6px",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 14, color: tc.text.secondary }} />
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}
